import { useEffect, useState } from 'react'
import { api } from '../api/client'

interface Stand {
  schluesselGesetzt: boolean
  schluesselGeaendertAm: string | null
  betriebe: { kuerzel: string; name: string; domains: string[] }[]
  wartendeAuslesungen: number
  hausdienst: {
    bezeichnung: string
    letzteMeldungAm: string | null
    letzteKennung: string | null
    ausfallSeit: string | null
    letzterAlarmAm: string | null
    alarme: number
    ueberwacht: boolean
    alarmNachMinuten: number
    alarmIntervallMinuten: number
    empfaenger: string
  } | null
}

function vorWieLange(iso: string | null): string {
  if (!iso) return 'noch nie'
  const minuten = Math.round((Date.now() - new Date(iso).getTime()) / 60000)
  if (minuten < 1) return 'gerade eben'
  if (minuten < 60) return `vor ${minuten} Minuten`
  const stunden = Math.floor(minuten / 60)
  if (stunden < 24) return `vor ${stunden} Stunden`
  return new Date(iso).toLocaleString('de-DE')
}

/**
 * Der Posteingang für Verkehrsmeldungen, aus Sicht des Plattformbetreibers.
 *
 * Hier entsteht der Schlüssel, mit dem sich der Hausdienst meldet. Die Zuordnung der
 * Domains pflegt jeder Betrieb selbst in seinen Betriebseinstellungen — hier steht nur,
 * was eingetragen ist, damit eine doppelte Domain auffällt.
 */
export function Posteingang() {
  const [stand, setStand] = useState<Stand | null>(null)
  const [neuerSchluessel, setNeuerSchluessel] = useState<string | null>(null)
  const [meldung, setMeldung] = useState<{ art: string; text: string } | null>(null)

  async function laden() {
    try {
      setStand(await api.get<Stand>('/api/plattform/posteingang'))
    } catch (ex) {
      setMeldung({ art: 'fehler', text: ex instanceof Error ? ex.message : 'Laden fehlgeschlagen' })
    }
  }

  useEffect(() => {
    void laden()
  }, [])

  async function erzeugen() {
    if (
      stand?.schluesselGesetzt &&
      !confirm('Einen neuen Schlüssel erzeugen? Der Hausdienst wird abgewiesen, bis er den neuen hat.')
    ) {
      return
    }
    try {
      const r = await api.post<{ schluessel: string }>('/api/plattform/posteingang/schluessel')
      setNeuerSchluessel(r.schluessel)
      await laden()
    } catch (ex) {
      setMeldung({ art: 'fehler', text: ex instanceof Error ? ex.message : 'Fehlgeschlagen' })
    }
  }

  const doppelt = new Set(
    (stand?.betriebe ?? [])
      .flatMap((b) => b.domains)
      .filter((d, i, alle) => alle.indexOf(d) !== i),
  )

  return (
    <>
      <div className="inhalt-kopf">
        <div>
          <h1>Posteingang</h1>
          <p>
            Der Hausdienst holt weitergeleitete Verkehrsmeldungen ab, liest sie mit dem
            KI-Server im Haus aus und legt sie hier in den Eingang des Betriebs.
          </p>
        </div>
      </div>

      {meldung && <div className={`meldung ${meldung.art}`}>{meldung.text}</div>}

      {stand?.hausdienst && (
        <div className="karte abschnitt">
          <h2>
            {stand.hausdienst.bezeichnung}{' '}
            {stand.hausdienst.ausfallSeit ? (
              <span className="etikett rot">meldet sich nicht</span>
            ) : stand.hausdienst.letzteMeldungAm ? (
              <span className="etikett gruen">meldet sich</span>
            ) : (
              <span className="etikett grau">noch nie gemeldet</span>
            )}
          </h2>
          <dl className="dienststand">
            <dt>Zuletzt gemeldet</dt>
            <dd>{vorWieLange(stand.hausdienst.letzteMeldungAm)}</dd>
            <dt>Kennung</dt>
            <dd>{stand.hausdienst.letzteKennung ?? '–'}</dd>
            <dt>Überwachung</dt>
            <dd>
              {stand.hausdienst.ueberwacht
                ? `Alarm nach ${stand.hausdienst.alarmNachMinuten} Minuten Stille, Wiederholung alle ${stand.hausdienst.alarmIntervallMinuten} Minuten an ${stand.hausdienst.empfaenger}`
                : 'abgeschaltet — unter Parameter einzurichten'}
            </dd>
            {stand.hausdienst.ausfallSeit && (
              <>
                <dt>Ausfall seit</dt>
                <dd>
                  {new Date(stand.hausdienst.ausfallSeit).toLocaleString('de-DE')} ·{' '}
                  {stand.hausdienst.alarme} Alarm(e) verschickt
                </dd>
              </>
            )}
          </dl>
        </div>
      )}

      <div className="karte abschnitt">
        <h2>Schlüssel des Hausdienstes</h2>
        <p>
          {stand?.schluesselGesetzt
            ? `Ein Schlüssel ist hinterlegt${stand.schluesselGeaendertAm ? ` (seit ${new Date(stand.schluesselGeaendertAm).toLocaleString('de-DE')})` : ''}.`
            : 'Noch kein Schlüssel — der Hausdienst wird abgewiesen.'}
        </p>
        {neuerSchluessel && (
          <div className="meldung hinweis">
            <strong>Nur jetzt sichtbar.</strong> In der Oberfläche des Hausdienstes unter
            „Portal“ eintragen:
            <pre className="schluessel-anzeige">{neuerSchluessel}</pre>
          </div>
        )}
        <button className="knopf-akzent" onClick={() => void erzeugen()}>
          {stand?.schluesselGesetzt ? 'Neuen Schlüssel erzeugen' : 'Schlüssel erzeugen'}
        </button>
      </div>

      <div className="abschnitt">
        <h2>Zuordnung der Betriebe</h2>
        <p style={{ color: 'var(--grau-700)' }}>
          Jeder Betrieb trägt in seinen Betriebseinstellungen ein, von welchen Domains er
          weiterleitet. Eine Domain bei zwei Betrieben wird abgewiesen.
        </p>
        <div className="tabelle-huelle">
          {!stand || stand.betriebe.length === 0 ? (
            <div className="leer">Noch kein Betrieb hat eine Domain eingetragen.</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Betrieb</th>
                  <th>Domains</th>
                </tr>
              </thead>
              <tbody>
                {stand.betriebe.map((b) => (
                  <tr key={b.kuerzel}>
                    <td>
                      <strong>{b.name}</strong> <span style={{ color: 'var(--grau-500)' }}>{b.kuerzel}</span>
                    </td>
                    <td>
                      {b.domains.map((d) => (
                        <span
                          key={d}
                          className={`etikett ${doppelt.has(d) ? 'rot' : 'navy'}`}
                          style={{ marginRight: 6 }}
                          title={doppelt.has(d) ? 'Auch bei einem anderen Betrieb eingetragen' : undefined}
                        >
                          {d}
                        </span>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {stand && stand.wartendeAuslesungen > 0 && (
          <p style={{ marginTop: 10 }}>
            <span className="etikett orange">{stand.wartendeAuslesungen}</span> hochgeladene
            PDFs warten auf den Hausdienst.
          </p>
        )}
      </div>
    </>
  )
}
